import { defineStore } from "pinia";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import useFanPublicationStore from "%/dynamic-component-editor/stores/fan-publication-store";
import { useCompletionStatusStore } from "%/dynamic-component-editor/stores/completion-status-store";
import { Context } from "%/dynamic-component-editor/classes/context";
import ProvidesContextEntry from "%/dynamic-component-editor/classes/provides-context-entry";
import { STORY_ENTITY_TYPE } from "%/dynamic-component-editor/classes/platform-standard-entities";

const URL_CONTINUE_ACTIVITY = "/story/activity/continue/publication/%d";
const URL_WORKOUT_SETS = "/story/%d/workout-sets";
const URL_REMOVE_WORKOUT_SET = "/story/%d/workout-sets/remove";

let continueTargetRequest = null;
const setLogRequests = new Map();

function serverMessage(error) {
  return error?.response?.data?.message || error?.message || "no response";
}

function fillStandardStoryContext(standardEntity, storyId) {
  if (!storyId) {
    return;
  }
  Context.addContext(standardEntity, {
    ...Context.getContext(standardEntity),
    entityType: STORY_ENTITY_TYPE,
    entitySource: ProvidesContextEntry.KEY_STANDARD,
    standardEntity,
    entityPkUrlVars: { id: storyId },
  });
}

function normalizeSet(set) {
  return {
    setNumber: Number(set.set_number) || 0,
    reps: Number(set.reps) || 0,
    weight: Number(set.weight) || 0,
    durationSeconds: Number(set.duration_seconds) || 0,
  };
}

export const useContinueActivityStore = defineStore("continue-activity-store", {
  state: () => ({
    continueStoryId: 0,
    lastStoryId: 0,
    programStoryId: 0,
    programComplete: false,
    continueTargetLoaded: false,
    setsByStory: {},
    prescribedSetsByStory: {},
    savingSetKeys: {},
  }),
  getters: {
    hasSignedInUser() {
      return useGeneralStore().isLoggedIn;
    },
    hasSomethingToContinue() {
      return this.continueStoryId > 0;
    },
    isSavingSet() {
      return (storyId, setNumber) => Boolean(this.savingSetKeys[`${storyId}.${setNumber}`]);
    },
  },
  actions: {
    loadContinueTarget() {
      if (continueTargetRequest) {
        return continueTargetRequest;
      }
      const publicationId = Number(useFanPublicationStore().publicationId) || 0;
      if (!this.hasSignedInUser || !publicationId) {
        this.continueTargetLoaded = true;
        continueTargetRequest = Promise.resolve();
        return continueTargetRequest;
      }
      continueTargetRequest = this.apiGet(
        URL_CONTINUE_ACTIVITY.replace("%d", publicationId),
        {},
        target => {
          this.continueStoryId = Number(target?.story_id) || 0;
          this.lastStoryId = Number(target?.last_story_id) || 0;
          this.programStoryId = Number(target?.program_story_id) || 0;
          this.programComplete = Boolean(target?.program_complete);
          fillStandardStoryContext(ProvidesContextEntry.KEY_STANDARD_CONTINUE_ACTIVITY, this.continueStoryId);
          if (this.continueStoryId) {
            fillStandardStoryContext(ProvidesContextEntry.KEY_STANDARD_CONTINUE_PROGRAM, this.programStoryId);
          }
          this.continueTargetLoaded = true;
        },
        error => {
          console.error(`[continue-activity-store] could not load what to continue: ${serverMessage(error)}`); // eslint-disable-line no-console
          this.continueTargetLoaded = true;
        },
      );
      return continueTargetRequest;
    },
    loadSetLog(storyId) {
      if (!storyId || !this.hasSignedInUser) {
        return Promise.resolve();
      }
      if (setLogRequests.has(storyId)) {
        return setLogRequests.get(storyId);
      }
      const request = this.apiGet(
        URL_WORKOUT_SETS.replace("%d", storyId),
        {},
        setLog => this.applySetLog(storyId, setLog),
        error => {
          console.error(`[continue-activity-store] could not load the logged sets for story ${storyId}: ${serverMessage(error)}`); // eslint-disable-line no-console
        },
      );
      setLogRequests.set(storyId, request);
      return request;
    },
    logSet(storyId, set) {
      return this.writeSet(storyId, set.setNumber, URL_WORKOUT_SETS, {
        set_number: set.setNumber,
        reps: set.reps,
        weight: set.weight,
        duration_seconds: set.durationSeconds,
      });
    },
    removeSet(storyId, setNumber) {
      return this.writeSet(storyId, setNumber, URL_REMOVE_WORKOUT_SET, { set_number: setNumber });
    },
    writeSet(storyId, setNumber, urlPattern, body) {
      if (!storyId || !this.hasSignedInUser) {
        return Promise.resolve();
      }
      const savingKey = `${storyId}.${setNumber}`;
      this.savingSetKeys[savingKey] = true;
      return this.apiPost(
        urlPattern.replace("%d", storyId),
        body,
        setLog => {
          this.applySetLog(storyId, setLog);
          this.applyCompletion(storyId, setLog?.completion);
          delete this.savingSetKeys[savingKey];
        },
        error => {
          delete this.savingSetKeys[savingKey];
          console.error(`[continue-activity-store] saving set ${setNumber} of story ${storyId} failed: ${serverMessage(error)}`); // eslint-disable-line no-console
        },
      );
    },
    applySetLog(storyId, setLog) {
      const sets = Array.isArray(setLog?.sets) ? setLog.sets : [];
      this.setsByStory[storyId] = sets.map(normalizeSet);
      this.prescribedSetsByStory[storyId] = Number(setLog?.prescribed_sets) || 1;
    },
    applyCompletion(storyId, completion) {
      if (!completion?.completion_status) {
        return;
      }
      const completionStore = useCompletionStatusStore();
      completionStore.applyCompletionStatus(storyId, completion.completion_status);
      completionStore.applyAffectedFolders(completion.affected_folders);
    },
  },
});
